/**
 * Wirft einen Fehler, wenn ein Objekt `null` oder `undefined` ist.
 *
 * @remarks
 * Diese Funktion kommt zum Einsatz, wenn ein Wert zurückgegeben werden
 * soll, der potenziell `null` oder `undefined` sein kann.
 *
 * @example
 * Das nachfolgende Beispiel verwendet `orElseThrow`, um eine Referenz auf ein
 * HTML Element zu erhalten.
 *
 * ```typescript
 * const element = document.getElementById("id") ||
 *     orElseThrow(() => new ReferenceError(`Element nicht vorhanden.`));
 * ```
 *
 * @param error - Ein Fehler.
 *
 * @throws
 * Einen `Error`, wenn ein Fehler aufgetreten ist.
 *
 * @public
 *
 */
const orElseThrow = <T extends Error>(error: () => T): never => {
  throw error();
};

/**
 * Gibt eine Referenz auf ein HTML Element zurück, dessen `id`-Attribut den
 * angegebenen Wert enthält.
 *
 * @remarks
 * Der Unterschied zwischen `getElementById` und `document.getElementById` liegt
 * im Umgang mit dem Rückgabewert `null`.
 *
 * Bei der Verwendung von `document.getElementById` ist eine Prüfung auf den
 * Rückgabewert `null` erforderlich. Bei der Verwendung von `getElementById`
 * nicht.
 *
 * @example
 * Das nachfolgende Beispiel demonstriert die Vorteile von `getElementById`
 * gegenüber `document.getElementById`.
 *
 * **Verwendung von `getElementById`**
 *
 * _Beachte:_ Keine Prüfung auf `null` erforderlich.
 *
 * ```typescript
 * const search = getElementById<HTMLFormElement>("search"); // HTMLFormElement
 * ```
 *
 * **Verwendung von `document.getElementById`**
 *
 * _Beachte:_ Prüfung auf `null` erforderlich.
 *
 * ```typescript
 * const search = document.getElementById("search"); // HTMLElement | null
 * ```
 *
 * @param id - Der Wert von einem `id`-Attribut.
 * @returns Das HTML Element mit der angegebenen `id`.
 *
 * @throws
 * Einen `ReferenceError`, wenn das HTML Element mit der angegebenen `id` nicht
 * existiert.
 *
 * @public
 */
const getElementById = <T extends HTMLElement>(id: HTMLElement["id"]) => {
  return (
    <T>document.getElementById(id) ||
    orElseThrow(() => new ReferenceError(`"#${id}" existiert nicht.`))
  );
};

/**
 * Gibt die statischen Eigenschaften einer Seite zurück.
 *
 * @remarks
 * Bei statischen Eigenschaften handelt es sich um serialisierte Daten
 * im `JSON` Format.
 *
 * Diese werden über ein HTML `<script>` Element in den HTML Quelltext einer
 * Seite eingefügt. Dabei gilt es zu beachten, dass dem HTML `<script>` Element
 * ein `id`-Attribut mit dem Wert `__STATIC_PROPS__` zugewiesen wird.
 *
 * @example
 * Das nachfolgende Beispiel zeigt, wie statische Eigenschaften durch die
 * Verwendung von `getStaticProps` aus dem HTML-Quellcode extrahiert werden
 * können.
 *
 * Füge die statische Eigenschaften in den HTML-Quellcode ein.
 *
 * ```html
 * <script id="__STATIC_PROPS__" type="application/json">
 *   [
 *    {
 *      "firstName": "John",
 *      "lastName": "Doe"
 *    },
 *    {
 *      "firstName": "Jane",
 *      "lastName": "Doe"
 *    }
 *   ]
 * </script>
 * ```
 *
 * Extrahiere die statischen Eigenschaften mit Hilfe von `getStaticProps`.
 *
 * ```typescript
 * const staticProps = getStaticProps<Array<{
 *   firstName: string,
 *   lastName: string
 * }>>();
 *
 * // Die deserialisierten statischen Eigenschaften.
 * console.log(staticProps);
 * ```
 *
 * @returns Die deserialisierten statischen Eigenschaften.
 *
 * @throws
 * Einen `ReferenceError`, wenn kein HTML `<script>` Element existiert, dessen
 * `id`-Attribut den Wert `__STATIC_PROPS__` enthält.
 *
 * @public
 */
const getStaticProps = <T>(): T => {
  const { innerText } = getElementById("__STATIC_PROPS__");

  return JSON.parse(innerText);
};

/**
 * Prüft, ob ein Array keine Elemente enthält.
 *
 * @param array - Ein Array.
 * @returns `true`, wenn das Array keine Elemente enthält.
 *          `false`, sonst.
 *
 * @public
 */
const isEmpty = <T>(array: Array<T>) => {
  return !Array.isArray(array) || !array.length;
};

export { getElementById, getStaticProps, isEmpty, orElseThrow };
